import type { QueryClient } from '@tanstack/react-query';
import type { Store } from '../data/store';
import { storesQueryOptions } from './query-options';

// The two ways the app learns a store is no longer its to open, and the one
// move both end in: re-read the stores list, then re-run the router against it
// so storeLayout's `!store.enabled` branch renders <DisabledStore/> and
// unmounts everything under it — the WebSocket provider included.
//
// They differ only in what the caller knows. A 402 is the API saying so, and
// needs nothing checked. A socket that keeps being refused is a hunch: a
// browser cannot read the status of a failed WebSocket handshake (the spec
// gives the `error` event none, deliberately), so a 402 on $connect and a
// dropped Wi-Fi packet look identical from the tab.

type RouterLike = { invalidate: () => unknown };

/**
 * Re-run the router against a freshly-invalidated stores list. For a caller
 * holding proof the store is gone — an HTTP 402 — so nothing is checked first.
 */
export function revalidateStoreAccessNow(queryClient: QueryClient, router: RouterLike): void {
  void queryClient.invalidateQueries({ queryKey: storesQueryOptions().queryKey });
  void router.invalidate();
}

/**
 * Re-read the stores list, bypassing its 12-hour staleTime, and re-run the
 * router only if this store has in fact been lost. For a caller working from a
 * hunch.
 *
 * The condition is what keeps this safe to call during a real outage: an
 * unconditional `router.invalidate()` re-runs `beforeLoad` and the loaders, and
 * a loader that fails mid-transition surfaces the route error screen in a tab
 * that was merely offline. A fetch that fails tells us nothing, so it does
 * nothing and leaves the next call to try again.
 */
export async function revalidateStoreAccessIfLost(
  queryClient: QueryClient,
  router: RouterLike,
  storeId: string
): Promise<void> {
  let stores: Store[];
  try {
    stores = await queryClient.fetchQuery({ ...storesQueryOptions(), staleTime: 0 });
  } catch {
    return;
  }

  const store = stores.find((s) => s.id === storeId);
  if (store?.enabled) {
    return;
  }

  void router.invalidate();
}
